import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthenticateProvider";
import { connectSocket, disconnectSocket, getSocket } from "../Helpers/socketClient";
import { SESSION_END_CODES, endSession, hasToken } from "../Helpers/webSession";

const SocketPresenceContext = createContext(null);


function SocketPresenceProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [connectionStatus, setConnectionStatus] = useState("offline"); // "online" | "away" | "offline"
    const currentPageRef = useRef(window.location?.pathname || "/");

    const emitVisibility = useCallback((socket) => {
        if (!socket?.connected) return;
        if (document.visibilityState === "hidden") {
            socket.emit("staff:away");
            setConnectionStatus("away");
        } else {
            socket.emit("staff:online", { page: currentPageRef.current });
            setConnectionStatus("online");
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            disconnectSocket();
            setConnectionStatus("offline");
            return undefined;
        }

        const socket = connectSocket();

        const handleConnect = () => emitVisibility(socket);
        const handleDisconnect = () => setConnectionStatus("offline");
        // The server refuses a socket whose sign-in is over and says why (err.data.code): leave like any other 401.
        const handleConnectError = (error) => {
            handleDisconnect();
            const code = error?.data?.code;
            // hasToken: no cookie means another tab already signed out; there is nothing left to end here.
            if (SESSION_END_CODES.has(code) && hasToken()) endSession({ code, message: error?.message });
        };
        const handleVisibilityChange = () => emitVisibility(socket);

        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.on("connect_error", handleConnectError);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        if (socket.connected) emitVisibility(socket);

        return () => {
            socket.off("connect", handleConnect);
            socket.off("disconnect", handleDisconnect);
            socket.off("connect_error", handleConnectError);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [isAuthenticated, emitVisibility]);

    useEffect(() => {
        const handlePageHide = () => {
            const socket = getSocket();
            if (socket.connected) socket.emit("staff:offline");
        };
        window.addEventListener("pagehide", handlePageHide);
        return () => window.removeEventListener("pagehide", handlePageHide);
    }, []);

    const reportPageChange = useCallback((pathname) => {
        currentPageRef.current = pathname;
        const socket = getSocket();
        if (socket.connected) socket.emit("staff:page-change", { page: pathname });
    }, []);

    return (
        <SocketPresenceContext.Provider value={{ connectionStatus, reportPageChange }}>
            {children}
        </SocketPresenceContext.Provider>
    );
}

export default SocketPresenceProvider;

export const useSocketPresence = () => useContext(SocketPresenceContext);
